import { Fragment, FC, useContext } from 'react';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { Flex } from 'components/Flex';
import {
  BKJBadge,
  BKJButton,
  BKJIconTooltip,
  BKJLoadingSpinner,
  BKJThemeColorType,
} from '@bookjane2/bookjane-design-library';
import {
  employeeReadableDate,
  getLanguageString,
  humanReadableDate,
  janeModalRoutingService,
} from 'components/JaneModal/JaneModal.constants';
import {
  JaneName,
  Text,
  H4,
  ValueText,
  LabelText,
  GeneralJaneWrapper,
  JaneAvatarWrapper,
  ColorDot,
  ContactJaneWrapper,
  MobileAppJaneWrapper,
  LoadingWrapper,
  PendingSignUpText,
  NonAppUserWrapper,
  EllipsisText,
  FieldWrapper,
  SectionWrapper,
  HeadSection,
  EmptyWrapper,
} from 'components/JaneModal/views/JaneDetailsView/GeneralTab/GeneralTab.styled';
import { useTranslation } from 'react-i18next';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import JaneMenu from 'components/JaneModal/JaneMenu';
import { useFetchJaneByIdDataContext } from 'components/JaneModal/FetchJaneById.data.context';
import { Jane } from 'types/graphql';
import { maskStringToPhoneString } from 'utils';
import { TeamMemberJaneAvatar } from 'components/TeamMemberJaneAvatar';

import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { TooltipWrapper } from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { getLocalizedDateFormat } from 'utils/getLocalizedDateFormat';
import { FeatureGuard } from 'guards/FeatureGuard';
import { useSelector } from 'react-redux';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { isMultiLocationFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors/isMultiLocationFeatureEnabledSelector';
import { TeamMemberPageContext } from 'pages/TeamMemberPage/TeamMemberPage.context';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { SessionService } from 'services';
import { StarRating } from 'components/StarRating';
import { startCase } from 'utils/startCase';
import { CommunitySeniorityMethodContext } from 'pages/TeamMemberPage/CommunitySeniorityMethod.context';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import {
  shiftModalGeneralTabValueTextMarginLeft,
  displayedSeniorityLength,
  secondaryLocationSchedulingFilter,
  secondaryLocationCallOutFilter,
} from 'components/JaneModal/views/JaneDetailsView/GeneralTab/GeneralTab.constants';
import i18next from 'i18next';

export const GeneralTab: FC = () => {
  const { t } = useTranslation();
  const isUserAgency = useSelector(isUserAgencySelector);
  const {
    values: { location: selectedLocation },
  } = useContext(TeamMemberPageContext);
  const {
    data: {
      fetchCommunity: {
        organization: { seniorityMethod },
      },
    },
  } = useContext(CommunitySeniorityMethodContext);
  const {
    data,
    dotColor,
    showDeactivateConfirmModal,
    setShowDeactivateConfirmModal,
    deactivateTeamMember,
    reactivateTeamMember,
    resendInviteTeamMember,
    setShowPendingSignUpModal,
    showPendingSignUpModal,
  }: {
    data: Jane;
    dotColor: BKJThemeColorType;
    showDeactivateConfirmModal: boolean;
    setShowDeactivateConfirmModal: (showDeactivateConfirmModal: boolean) => void;
    deactivateTeamMember: () => void;
    reactivateTeamMember: () => void;
    resendInviteTeamMember: () => void;
    showPendingSignUpModal: boolean;
    setShowPendingSignUpModal: (showPendingSignUpModal: boolean) => void;
  } = useFetchJaneByIdDataContext();
  const {
    values: {
      location: { value: locationId },
    },
  } = useContext(TeamMemberPageContext);
  const isMultiLocationFeatureEnabled = useSelector(
    isMultiLocationFeatureEnabledSelector(Number(locationId)),
  );
  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(locationId)),
  );
  const isMultiLocationSchedulingEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(locationId)),
  );
  const isMultiLocationCallOutEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(locationId)),
  );

  if (!data)
    return (
      <LoadingWrapper>
        <BKJLoadingSpinner variant="Default" />
      </LoadingWrapper>
    );

  const {
    avatarUrls = { origin: '' },
    accessStatus = { name: '' },
    primaryPosition = { details: { name: '' } },
    communityName = '',
    primaryCommunity = { id: '' },
    usersCommunities = [],
    secondaryPositions = [],
    employmentStatus = { name: '' },
    phone = '',
    email = '',
    location = { fullAddress: '' },
    shorthandName = '',
    locale = '',
    lastActivityAt = '',
    customerReviews = { averageRating: '', count: '' },
    pendingSignup = false,
    isMobile = false,
    employeeNumber = '',
    birthDay = '',
    birthMonth = '',
    hourlyRate = '',
    employeeStartDate = '',
    rankingSeniorityDate,
    organizationSeniorityRanking,
  } = data;
  const primaryCommunityId = primaryCommunity?.id;

  const isJanePrimaryLocationSame =
    getCurrentlySelectedLocation()?.value?.toString() === primaryCommunityId;
  const showEdit = isJanePrimaryLocationSame || SessionService.assertUserType('Agency');

  const averageRating = !customerReviews?.count ? 0 : Number(customerReviews?.averageRating) || 0;
  const birth_Month = birthMonth ? birthMonth : 1; // just to avoid null
  const { month } = getLocalizedDateFormat(new Date(2000, birth_Month - 1).toISOString());
  const formattedHourlyRate: string = hourlyRate ? `$${Number(hourlyRate).toFixed(2)}` : '-';

  const seniorityLength = displayedSeniorityLength(seniorityMethod, rankingSeniorityDate, t);

  return (
    <Fragment>
      <BKJCenteredDialog
        isOpen={showDeactivateConfirmModal}
        variant="Danger"
        title={t('team_member:DEACTIVATE_TEAM_MEMBER')}
        text={
          isUserAgency
            ? t('team_member:THIS_WILL_BLOCK_THE_TEAM_MEMBER_FROM_ACCESSING')
            : t('team_member:BEFORE_DEACTIVATING_THE_TEAM_MEMBER')
        }
        cancelLabel={i18next.t('auth:CANCEL')}
        actionLabel={i18next.t('team_member:DEACTIVATE')}
        onAction={deactivateTeamMember}
        onCancel={() => setShowDeactivateConfirmModal(false)}
        onClose={() => setShowDeactivateConfirmModal(false)}
      />
      <HeadSection>
        <JaneAvatarWrapper>
          <TeamMemberJaneAvatar
            url={avatarUrls?.origin}
            status={accessStatus?.name}
            size={112}
            pendingSignup={pendingSignup}
          />
        </JaneAvatarWrapper>
        <Flex justifyContent="space-between" width="100%">
          <Flex flexDirection="column" gap="15px" width="100%">
            <Flex justifyContent="space-between">
              <JaneName>{shorthandName}</JaneName>
              {showEdit && (
                <FeatureGuard NOT type="FEATURE_TYPE_ACCUSHIELD_SYNCING">
                  <JaneMenu
                    disabled={false}
                    setShowDeactivateConfirmModal={setShowDeactivateConfirmModal}
                    accessStatusName={accessStatus?.name}
                    reactivateTeamMember={reactivateTeamMember}
                  />
                </FeatureGuard>
              )}
            </Flex>
            <Flex alignItems="center" margin="0 0 0 4px">
              <ColorDot color={dotColor}>•</ColorDot>
              <Text>
                {accessStatus.name === 'blocked'
                  ? t('team_member:Deactivated')
                  : t(`team_member:${startCase(accessStatus?.name)}`)}
              </Text>
              {pendingSignup && accessStatus?.name === 'approved' && (
                <PendingSignUpText>
                  ({i18next.t('team_member:PENDING_MOBILE_APP_SIGN_UP')})
                </PendingSignUpText>
              )}
            </Flex>
            <EllipsisText>{primaryPosition?.details?.name}</EllipsisText>
            {isMobile && (
              <NonAppUserWrapper>
                <Flex>
                  <BKJBadge variant="Grey"> {t('team_member:Non_App_User')}</BKJBadge>
                  <BKJIconTooltip iconName="Info" placement="bottom">
                    <TooltipWrapper>
                      {i18next.t('team_member:STAFF_WILL_RECEIVE_AUTOMATED_PHONE')}
                    </TooltipWrapper>
                  </BKJIconTooltip>
                </Flex>
              </NonAppUserWrapper>
            )}
            <div>
              <StarRating rating={averageRating} />
            </div>
          </Flex>
        </Flex>
      </HeadSection>
      <GeneralJaneWrapper>
        <SectionWrapper>
          <H4>{t('team_member:GENERAL_PROFILE')}</H4>
          {isMultiLocationTieringEnabled && (
            <Flex justifyContent="space-between">
              <FieldWrapper>
                <LabelText>{t('team_member:SENIORITY_RANKING')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {organizationSeniorityRanking || '-'}
                </ValueText>
              </FieldWrapper>

              <FieldWrapper>
                <LabelText>{t('team_member:SENIORITY_LENGTH')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {seniorityLength}
                </ValueText>
              </FieldWrapper>
            </Flex>
          )}
          <FieldWrapper>
            <LabelText>{t('team_member:PRIMARY_LOCATION')}</LabelText>
            <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
              {communityName}
            </ValueText>
          </FieldWrapper>
          {isMultiLocationFeatureEnabled && (
            <Fragment>
              {isMultiLocationSchedulingEnabled && (
                <FieldWrapper>
                  <LabelText>{t('team_member:SECONDARY_LOCATIONS_SCHEDULING')}</LabelText>
                  {usersCommunities?.filter(secondaryLocationSchedulingFilter)?.length ? (
                    usersCommunities
                      .filter(secondaryLocationSchedulingFilter)
                      .map((secondaryLocation) => (
                        <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                          {secondaryLocation.community.name}
                        </ValueText>
                      ))
                  ) : (
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
                  )}
                </FieldWrapper>
              )}
              {(isMultiLocationCallOutEnabled || isMultiLocationTieringEnabled) && (
                <FieldWrapper>
                  <LabelText>{t('team_member:SECONDARY_LOCATIONS_CALL_OUT')}</LabelText>
                  {usersCommunities?.filter(secondaryLocationCallOutFilter)?.length ? (
                    usersCommunities
                      .filter(secondaryLocationCallOutFilter)
                      .map((secondaryLocation) => (
                        <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                          {secondaryLocation.community.name}
                        </ValueText>
                      ))
                  ) : (
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
                  )}
                </FieldWrapper>
              )}
            </Fragment>
          )}
          <FieldWrapper>
            <LabelText>{t('team_member:PRIMARY_POSITION')}</LabelText>
            <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
              {primaryPosition?.details?.name}
            </ValueText>
          </FieldWrapper>
          <FieldWrapper>
            <LabelText>{t('team_member:SECONDARY_POSITIONS')}</LabelText>
            {secondaryPositions.length > 0 ? (
              secondaryPositions.map((position, key) => (
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft} key={key}>
                  {position?.details?.name}
                </ValueText>
              ))
            ) : (
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
            )}
          </FieldWrapper>
          <Flex justifyContent="space-between">
            <UserGuard
              userType={[
                UserPermissionEnum.USER_TYPE_B2B,
                UserPermissionEnum.USER_TYPE_ESSENTIAL,
                UserPermissionEnum.USER_TYPE_PREMIUM,
              ]}
            >
              <FieldWrapper>
                <LabelText>{t('team_member:EMPLOYMENT_STATUS')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {t(`team_member:${startCase(employmentStatus?.name)}`)}
                </ValueText>
              </FieldWrapper>
            </UserGuard>

            <FieldWrapper>
              <LabelText>{t('team_member:EMPLOYEE_ID')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                {employeeNumber || '-'}
              </ValueText>
            </FieldWrapper>
          </Flex>
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_B2B,
              UserPermissionEnum.USER_TYPE_ESSENTIAL,
              UserPermissionEnum.USER_TYPE_PREMIUM,
            ]}
          >
            <Flex justifyContent="space-between">
              <FieldWrapper>
                <LabelText>{t('team_member:HOURLY_RATE')}</LabelText>
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                  {formattedHourlyRate || '-'}
                </ValueText>
              </FieldWrapper>
            </Flex>
          </UserGuard>
        </SectionWrapper>
      </GeneralJaneWrapper>
      <ContactJaneWrapper>
        <SectionWrapper>
          <H4>{t('team_member:CONTACT_INFORMATION')}</H4>
          <Flex justifyContent="space-between">
            <FieldWrapper>
              <LabelText>{t('team_member:PHONE_NUMBER')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                {maskStringToPhoneString(phone)}
              </ValueText>
            </FieldWrapper>
            <FieldWrapper>
              <LabelText>{t('team_member:EMAIL')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>{email}</ValueText>
            </FieldWrapper>
          </Flex>
          <FieldWrapper>
            <LabelText>{t('team_member:HOME_ADDRESS')}</LabelText>
            <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
              {location?.fullAddress}
            </ValueText>
          </FieldWrapper>
        </SectionWrapper>
      </ContactJaneWrapper>
      {WhiteLabelAccessor.isPetalProvider && (
        <ContactJaneWrapper>
          <SectionWrapper>
            <FieldWrapper>
              <LabelText>{t('team_member:LANGUAGE_PREFERENCE')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                {getLanguageString(locale)}
              </ValueText>
            </FieldWrapper>
          </SectionWrapper>
        </ContactJaneWrapper>
      )}
      <ContactJaneWrapper>
        <SectionWrapper>
          <Flex justifyContent="space-between">
            <FieldWrapper>
              <LabelText>{t('team_member:BIRTH_MONTH')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                <EmptyWrapper>{birthMonth ? month : '-'}</EmptyWrapper>
              </ValueText>
            </FieldWrapper>
            <FieldWrapper>
              <LabelText>{t('team_member:BIRTH_DAY')}</LabelText>
              <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                <EmptyWrapper>{birthDay ? birthDay : '-'}</EmptyWrapper>
              </ValueText>
            </FieldWrapper>
          </Flex>
        </SectionWrapper>
      </ContactJaneWrapper>
      <ContactJaneWrapper>
        <SectionWrapper>
          <FieldWrapper>
            <LabelText>{t('team_member:EMPLOYEE_START_DATE')}</LabelText>
            <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
              {employeeStartDate ? (
                <Fragment>
                  {employeeReadableDate(employeeStartDate, selectedLocation?.timeZone)}
                </Fragment>
              ) : (
                '-'
              )}
            </ValueText>
          </FieldWrapper>
        </SectionWrapper>
      </ContactJaneWrapper>
      <MobileAppJaneWrapper>
        <SectionWrapper>
          <FieldWrapper>
            <LabelText>{t('team_member:MOBILE_APP_STATUS')}</LabelText>
            <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
              {lastActivityAt ? (
                <Fragment>
                  {t('team_member:LAST_USED_AT') + ' '}
                  {humanReadableDate(lastActivityAt, selectedLocation?.timeZone)}
                </Fragment>
              ) : (
                '-'
              )}
            </ValueText>
          </FieldWrapper>
        </SectionWrapper>
      </MobileAppJaneWrapper>
      <BKJSideModalFooter gap="8px" justifyContent="end">
        <FeatureGuard NOT type="FEATURE_TYPE_ACCUSHIELD_SYNCING">
          {accessStatus?.name === 'blocked' ? (
            showEdit && (
              <BKJButton
                variant="PurpleOutlined"
                width="180px"
                onClick={() => reactivateTeamMember()}
                type="button"
              >
                {t('team_member:RE_ACTIVATE')}
              </BKJButton>
            )
          ) : (
            <Fragment>
              {pendingSignup && showEdit && (
                <BKJButton
                  variant="PurpleOutlined"
                  width="fit-content"
                  onClick={() => setShowPendingSignUpModal(!showPendingSignUpModal)}
                  type="button"
                >
                  {t('team_member:RESEND_INVITE')}
                </BKJButton>
              )}

              {showEdit && (
                <BKJButton
                  variant="PurpleSolid"
                  width="180px"
                  onClick={() => janeModalRoutingService.goToView('EditJaneView')}
                  type="button"
                >
                  {t('team_member:EDIT')}
                </BKJButton>
              )}
            </Fragment>
          )}
        </FeatureGuard>
      </BKJSideModalFooter>
      <BKJCenteredDialog
        isOpen={showPendingSignUpModal}
        variant="Default"
        title={t('team_member:RE_SEND_SIGN_UP_INVITE')}
        text={t('team_member:RE_SEND_SIGN_UP_INVITE_DESCRIPTION')}
        cancelLabel={t('groups:CANCEL')}
        actionLabel={t('team_member:SEND')}
        onAction={() => {
          resendInviteTeamMember();
          setShowPendingSignUpModal(false);
        }}
        onCancel={() => setShowPendingSignUpModal(false)}
        onClose={() => setShowPendingSignUpModal(false)}
      />
    </Fragment>
  );
};
